import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { updateDriverStatus } from '../../services/driverService';
import { Driver } from '../../types/driver';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';

interface DriverStatusUpdateProps {
  currentDriver?: Driver;
  onStatusUpdate?: (updatedDriver: Driver) => void;
}

const DriverStatusUpdate: React.FC<DriverStatusUpdateProps> = ({
  currentDriver,
  onStatusUpdate,
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [status, setStatus] = useState<'Active' | 'Inactive' | 'On Delivery'>(currentDriver?.status as 'Active' | 'Inactive' | 'On Delivery' || 'Active');
  const [currentLocation, setCurrentLocation] = useState(currentDriver?.currentLocation || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleStatusUpdate = async () => {
    if (!user?.id) {
      setMessage({ type: 'error', text: t('drivers.statusUpdate.noUserError') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const updatedDriver = await updateDriverStatus(
        user.id.toString(),
        status,
        currentLocation || undefined
      );
      
      setMessage({ type: 'success', text: t('drivers.statusUpdate.success') });
      
      // Call the callback to update parent component
      if (onStatusUpdate) {
        onStatusUpdate(updatedDriver);
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      const errorMessage = error.response?.data?.message || error.message || t('drivers.statusUpdate.error');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusValue: string) => {
    switch (statusValue) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'default';
      case 'On Delivery':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusTranslation = (statusValue: string) => {
    switch (statusValue) {
      case 'Active':
        return t('drivers.statuses.active');
      case 'Inactive':
        return t('drivers.statuses.inactive');
      case 'On Delivery':
        return t('drivers.statuses.on_delivery');
      default:
        return statusValue;
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('drivers.statusUpdate.title')}
        </Typography>
        
        {currentDriver && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {t('drivers.statusUpdate.currentStatus')}
            </Typography>
            <Chip 
              label={getStatusTranslation(currentDriver.status)}
              color={getStatusColor(currentDriver.status)}
              size="small"
            />
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth>
            <InputLabel>{t('drivers.status')}</InputLabel>
            <Select
              value={status}
              label={t('drivers.status')}
              onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive' | 'On Delivery')}
              disabled={loading}
            >
              <MenuItem value="Active">{t('drivers.statuses.active')}</MenuItem>
              <MenuItem value="Inactive">{t('drivers.statuses.inactive')}</MenuItem>
              <MenuItem value="On Delivery">{t('drivers.statuses.on_delivery')}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={t('drivers.currentLocation')}
            value={currentLocation}
            onChange={(e) => setCurrentLocation(e.target.value)}
            disabled={loading}
            placeholder={t('drivers.statusUpdate.locationPlaceholder')}
            helperText={t('drivers.statusUpdate.locationHelper')}
          />

          {message && (
            <Alert 
              severity={message.type}
              icon={message.type === 'success' ? <CheckCircle /> : <Error />}
            >
              {message.text}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleStatusUpdate}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('drivers.statusUpdate.updating') : t('drivers.statusUpdate.updateButton')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DriverStatusUpdate;
